<template>
  <div class="success-container">
    <div class="success-content">
      <div class="success-icon">
        <div class="checkmark">✓</div>
      </div>

      <h1 class="success-title">{{ successTitle }}</h1>
      <p class="success-message">{{ successMessage }}</p>

      <div class="success-details" v-if="userDetails">
        <div class="detail-card">
          <h3>Account Details</h3>
          <div class="detail-item" v-if="userDetails.name">
            <span class="label">Name:</span>
            <span class="value">{{ userDetails.name }}</span>
          </div>
          <div class="detail-item" v-if="userDetails.email">
            <span class="label">Email:</span>
            <span class="value">{{ userDetails.email }}</span>
          </div>
          <div class="detail-item" v-if="userDetails.plan">
            <span class="label">Plan:</span>
            <span class="value">{{ userDetails.plan }}</span>
          </div>
        </div>
      </div>

      <div class="success-actions">
        <button @click="handlePrimaryAction" class="primary-button">
          {{ primaryButtonText }}
        </button>
      </div>

      <div class="footer-links">
        <a href="https://quali-d.com/support" target="_blank" class="support-link">
          Need help? Contact Support
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { AuthService } from '../services/auth.js'

export default {
  name: 'PaymentSuccessView',
  data () {
    return {
      isRenewal: false,
      userDetails: null,
      loading: true,
      loggingIn: false
    }
  },
  computed: {
    successTitle () {
      return this.isRenewal ? 'Renewal Successful!' : 'Registration Successful!'
    },
    successMessage () {
      if (this.loggingIn) {
        return 'Logging you in automatically...'
      }
      return this.isRenewal
        ? 'Your subscription has been renewed successfully. You can continue using all features without interruption.'
        : 'Welcome to QUALI-D! Your account has been created and your payment has been processed successfully. Logging you in...'
    },
    primaryButtonText () {
      return this.isRenewal ? 'Go to Dashboard' : 'Start Using QUALI-D'
    }
  },
  async mounted () {
    this.isRenewal = this.$route.name === 'renewal-success'
    this.userDetails = this.$route.query.user ? JSON.parse(decodeURIComponent(this.$route.query.user)) : null

    // For new registrations, automatically log the user in
    if (!this.isRenewal) {
      await this.autoLogin()
    } else {
      // For renewals, auto-redirect after 5 seconds
      setTimeout(() => {
        this.handlePrimaryAction()
      }, 5000)
    }
  },
  methods: {
    async autoLogin () {
      // Get stored credentials
      const credentialsStr = localStorage.getItem('pendingLoginCredentials')

      if (!credentialsStr) {
        console.warn('No pending login credentials found')
        // Fallback to login page after delay
        setTimeout(() => {
          this.$router.push('/login?message=' + encodeURIComponent('Registration completed! Please log in with your credentials.'))
        }, 5000)
        return
      }

      try {
        this.loggingIn = true
        const credentials = JSON.parse(credentialsStr)

        console.log('Attempting auto-login with credentials:', {
          country_code: credentials.country_code,
          phone: credentials.phone
        })

        // Call login API
        const result = await AuthService.login(credentials)

        if (result.success || result.token) {
          // Save auth data
          AuthService.setAuthData(result)

          // Clear pending credentials
          localStorage.removeItem('pendingLoginCredentials')

          console.log('Auto-login successful, redirecting to homepage...')

          // Redirect to homepage
          setTimeout(() => {
            this.$router.push('/')
          }, 2000)
        } else {
          throw new Error('Login failed')
        }
      } catch (error) {
        console.error('Auto-login failed:', error)

        // Clear invalid credentials
        localStorage.removeItem('pendingLoginCredentials')

        // Redirect to login page with message
        setTimeout(() => {
          this.$router.push('/login?message=' + encodeURIComponent('Registration completed! Please log in with your credentials.'))
        }, 3000)
      } finally {
        this.loggingIn = false
      }
    },
    handlePrimaryAction () {
      if (this.isRenewal) {
        this.$router.push('/payments')
      } else {
        // For registrations, this button won't be shown as auto-login happens
        this.$router.push('/')
      }
    }
  }
}
</script>

<style scoped>
.success-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.success-content {
  background: white;
  border-radius: 20px;
  padding: 48px;
  max-width: 600px;
  width: 100%;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
}

.success-icon {
  margin-bottom: 32px;
}

.checkmark {
  width: 100px;
  height: 100px;
  background: #10b981;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 48px;
  color: white;
  font-weight: bold;
  animation: scaleIn 0.5s ease-out;
}

@keyframes scaleIn {
  from {
    transform: scale(0);
  }
  to {
    transform: scale(1);
  }
}

.success-title {
  font-size: 32px;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 16px 0;
}

.success-message {
  font-size: 18px;
  color: #6b7280;
  line-height: 1.6;
  margin: 0 0 32px 0;
}

.success-details {
  margin-bottom: 32px;
}

.detail-card {
  background: #f9fafb;
  border-radius: 12px;
  padding: 24px;
  border: 1px solid #e5e7eb;
}

.detail-card h3 {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 16px 0;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #e5e7eb;
}

.detail-item:last-child {
  border-bottom: none;
}

.detail-item .label {
  font-weight: 500;
  color: #6b7280;
}

.detail-item .value {
  font-weight: 600;
  color: #1f2937;
}

.success-actions {
  margin-bottom: 24px;
}

.primary-button {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
  border: none;
  padding: 16px 32px;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 200px;
}

.primary-button:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 25px rgba(16, 185, 129, 0.3);
}

.footer-links {
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
}

.support-link {
  color: #6b7280;
  text-decoration: none;
  font-size: 14px;
  transition: color 0.2s;
}

.support-link:hover {
  color: #10b981;
}

@media (max-width: 768px) {
  .success-content {
    padding: 32px 24px;
    margin: 16px;
  }

  .success-title {
    font-size: 24px;
  }

  .success-message {
    font-size: 16px;
  }

  .checkmark {
    width: 80px;
    height: 80px;
    font-size: 36px;
  }
}
</style>
